import {
  WSButton,
  WSElement,
  WSPage,
  WSPageToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import { WorkLogStatus } from "@wingspanhq/wingspan-api-sdk";
import { useCallback, useMemo } from "react";
import { Redirect, Route, Switch, useHistory } from "react-router-dom";
import { Tabs } from "../../../components/Tabs";
import { useQueryWorkLogsSummary } from "../../../query/workLog/queries/useQueryWorkLogsSummary";
import { PATH_PAYABLES } from "../../Payables/paths";
import { useModalCreateWorkLogPayer } from "../modals/ModalCreateWorkLogPayer";
import { WorklogsPayerDashboard } from "../components/WorklogsPayerDashboard";
import { FilterableTableWorkLogsPayerView } from "../components/TableWorkLogsPayerView/FilterableTableWorkLogsPayerView";
import { WorkLogPayerTabFilterStatus } from "../components/TableWorkLogsPayerView/filters/types";
import {
  routeHelpersWorkLogPayerDetails,
  routeHelpersWorkLogsPayer,
  routeHelpersWorkLogsPayerApproved,
  routeHelpersWorkLogsPayerDashboard,
  routeHelpersWorkLogsPayerRejected,
  routeHelpersWorkLogsPayerInProgress,
  routeHelpersWorkLogsPayerToApprove
} from "../paths";
import { useSession } from "@hooks/useSession";

export const RoutePayerLogs = () => {
  const history = useHistory();
  const { accountId } = useSession();
  const modalCreateWorkLog = useModalCreateWorkLogPayer();
  const goToWorkLogDetails = routeHelpersWorkLogPayerDetails.useGoTo();

  const handleCreateWorkLog = useCallback(async () => {
    const workLog = await modalCreateWorkLog.open();

    // Open the new work log so the payer can add work items straight away.
    if (workLog?.id) {
      goToWorkLogDetails({ workLogId: workLog.id });
    }
  }, [modalCreateWorkLog, goToWorkLogDetails]);

  const queryHasAnyWorkLogs = useQueryWorkLogsSummary({
    filter: { payerAccountId: { eq: accountId } }
  });
  const hasAnyWorkLogs = (queryHasAnyWorkLogs.data?.listSize ?? 0) > 0;

  // Summary queries for tab counts
  const queryToApprove = useQueryWorkLogsSummary({
    filter: {
      payerAccountId: { eq: accountId },
      status: { anyOf: [WorkLogStatus.IN_REVIEW] }
    }
  });

  const queryApproved = useQueryWorkLogsSummary({
    filter: {
      payerAccountId: { eq: accountId },
      status: { anyOf: [WorkLogStatus.APPROVED] }
    }
  });

  const queryInProgress = useQueryWorkLogsSummary({
    filter: {
      payerAccountId: { eq: accountId },
      status: { anyOf: [WorkLogStatus.IN_PROGRESS] }
    }
  });

  const queryRejected = useQueryWorkLogsSummary({
    filter: {
      payerAccountId: { eq: accountId },
      status: { anyOf: [WorkLogStatus.REJECTED] }
    }
  });

  // Build tabs based on work logs existence
  const tabs = useMemo(() => {
    const allTabs = [
      {
        label: "Dashboard",
        path: routeHelpersWorkLogsPayerDashboard.path
      },
      {
        label: "To approve",
        path: routeHelpersWorkLogsPayerToApprove.path,
        badge: queryToApprove.data?.listSize
      },
      {
        label: "Approved",
        path: routeHelpersWorkLogsPayerApproved.path,
        badge: queryApproved.data?.listSize
      },
      {
        label: "Rejected",
        path: routeHelpersWorkLogsPayerRejected.path,
        badge: queryRejected.data?.listSize
      },
      {
        label: "In-progress",
        path: routeHelpersWorkLogsPayerInProgress.path,
        badge: queryInProgress.data?.listSize
      }
    ];

    // Only show Dashboard tab when no work logs exist
    if (!hasAnyWorkLogs) {
      return [allTabs[0]];
    }

    return allTabs;
  }, [
    hasAnyWorkLogs,
    queryToApprove.data?.listSize,
    queryApproved.data?.listSize,
    queryInProgress.data?.listSize,
    queryRejected.data?.listSize
  ]);

  return (
    <WSPage>
      <WSElement mb="2XL">
        <WSPageToolbar
          title="Work logs"
          actions={[
            {
              label: "New work log",
              buttonKind: "Primary",
              icon: "plus",
              onClick: handleCreateWorkLog
            }
          ]}
        />
        <WSText.ParagraphSm color="gray500">
          Review contractor work logs before they become payables. Once
          approved, payables are automatically generated on your{" "}
          <WSButton.Link onClick={() => history.push(PATH_PAYABLES)}>
            Payables
          </WSButton.Link>{" "}
          page.
        </WSText.ParagraphSm>
      </WSElement>

      <Tabs mb="L" tabs={tabs} />
      <Switch>
        <Route
          path={routeHelpersWorkLogsPayerDashboard.path}
          component={WorklogsPayerDashboard}
        />
        <Route path={routeHelpersWorkLogsPayerToApprove.path}>
          <FilterableTableWorkLogsPayerView
            status={WorkLogPayerTabFilterStatus.ToApprove}
            enableBulkActions
          />
        </Route>
        <Route path={routeHelpersWorkLogsPayerApproved.path}>
          <FilterableTableWorkLogsPayerView
            status={WorkLogPayerTabFilterStatus.Approved}
          />
        </Route>
        <Route path={routeHelpersWorkLogsPayerRejected.path}>
          <FilterableTableWorkLogsPayerView
            status={WorkLogPayerTabFilterStatus.Rejected}
          />
        </Route>
        <Route path={routeHelpersWorkLogsPayerInProgress.path}>
          <FilterableTableWorkLogsPayerView
            status={WorkLogPayerTabFilterStatus.InProgress}
          />
        </Route>
        <Redirect
          from={routeHelpersWorkLogsPayer.path}
          to={routeHelpersWorkLogsPayerDashboard.path}
        />
      </Switch>
    </WSPage>
  );
};
